// Settings -> Sign-in (mock M2 states 15 to 23, 27 and 32): how the person signs in, adding and removing Google and
// Microsoft (the email link always stays), two-step sign-in with an authenticator app, and the last sign-in Omni
// recorded. Hidden while a Super admin acts as someone (states 36 and 37): how someone signs in is theirs alone. Also
// the Add Microsoft sign-in sheet Your accounts opens (mock M3 state 06).
import React, { useEffect, useState } from "react";
import { authCode, PROVIDERS, type Provider, type TwoStepSetup } from "../auth.js";
import { Icon } from "../icons.js";
import { Link } from "../router.js";
import { useSession } from "../session.js";
import { dayAt, errorText, firstName } from "../format.js";
import { Callout, Chip, Sheet, useToast } from "../ui.js";
import { IdpButton, LinkAlertView, PROVIDER_NAME, ProviderMark, addProvider, methodOf, signInProblem, type LinkAlert } from "./signin-bits.js";

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A QR code drawn from the encoder's modules, one square each, on white. */
function QrCode({ modules }: { modules: boolean[][] }): React.ReactElement {
  const size = modules.length;
  const d = modules.flatMap((row, y) => row.flatMap((on, x) => (on ? [`M${x} ${y}h1v1h-1z`] : []))).join("");
  return (
    <svg className="qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="QR code for an authenticator app" shapeRendering="crispEdges" data-testid="two-step-qr">
      <rect width={size} height={size} fill="#fff" />
      <path fill="#0A1620" d={d} />
    </svg>
  );
}

function TwoStepSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }): React.ReactElement {
  const { me, auth } = useSession();
  const [setup, setSetup] = useState<TwoStepSetup | null>(null);
  const [modules, setModules] = useState<boolean[][] | null>(null);
  const [code, setCode] = useState("");
  const [wrong, setWrong] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    auth.startTwoStep().then(
      async (started) => {
        if (!live) return;
        setSetup(started);
        // The encoder is a lazy chunk: only this sheet draws a QR code.
        const { encode } = await import("uqr");
        if (live) setModules(encode(started.uri, { ecc: "M", border: 4 }).data);
      },
      (err: unknown) => {
        if (live) setProblem(signInProblem(err));
      },
    );
    return () => {
      live = false;
    };
  }, [auth]);

  async function finish(): Promise<void> {
    if (!setup) return;
    setBusy(true);
    setWrong(false);
    setProblem(null);
    try {
      await setup.finish(code.replace(/\D/g, ""));
      onDone();
    } catch (err) {
      if (authCode(err) === "auth/invalid-verification-code") setWrong(true);
      else setProblem(signInProblem(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      title="Set up two-step sign-in"
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="two-step-turn-on" disabled={!setup || busy} onClick={() => void finish()}>
            <Icon name="shield-check" size={14} />
            Turn on two-step
          </button>
        </>
      }
    >
      {problem ? (
        <Callout kind="stop" icon="triangle-alert">
          {problem}
        </Callout>
      ) : null}
      <ol className="ts-steps">
        <li>
          <b>Open an authenticator app on your phone.</b>
          <span className="aside">Google Authenticator, Microsoft Authenticator, 1Password and Authy all work.</span>
        </li>
        <li>
          <b>Scan this code with it.</b>
          {modules ? <QrCode modules={modules} /> : <span className="qr wait">Getting a code</span>}
          {setup ? (
            <>
              <span className="aside">
                Can&rsquo;t scan it? Enter this key: <span className="key">{setup.key.replace(/(.{4})(?=.)/g, "$1 ")}</span>
              </span>
              <span className="aside">It shows up as Omni ({me.user.email}).</span>
            </>
          ) : null}
        </li>
        <li>
          <b>Enter the 6-digit code the app shows.</b>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void finish();
            }}
          >
            <label className="field">
              <span>Code</span>
              <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} data-testid="two-step-code" value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
            {wrong ? (
              <p className="signin-error" role="alert">
                That code didn&rsquo;t match. Enter the one your app shows now; it changes every 30 seconds.
              </p>
            ) : null}
          </form>
        </li>
      </ol>
      <p className="card-note">From then on, every sign-in asks for a code, whichever way you sign in.</p>
    </Sheet>
  );
}

type SheetOpen = { kind: "remove"; provider: Provider } | { kind: "totp" } | { kind: "totp-off" } | null;

export function SignInTab(): React.ReactElement {
  const { me, auth } = useSession();
  const toast = useToast();
  const [, setRound] = useState(0);
  const [sheet, setSheet] = useState<SheetOpen>(null);
  const [notice, setNotice] = useState<LinkAlert | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const email = me.user.email;
  const linked = auth.linkedProviders();
  const twoStep = auth.twoStep();
  const last = me.user.lastSignIn ?? null;
  const reread = () => setRound((r) => r + 1);

  async function add(provider: Provider): Promise<void> {
    setNotice(null);
    setBusy(true);
    const outcome = await addProvider(auth, provider, email);
    setBusy(false);
    reread();
    if (outcome.kind === "added") toast(`${PROVIDER_NAME[provider]} added. Continue with ${PROVIDER_NAME[provider]} now signs you in as ${outcome.email}.`);
    else if (outcome.kind === "refused") setNotice(outcome.alert);
  }

  async function run(change: () => Promise<void>, said: string): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      await change();
      setSheet(null);
      toast(said);
    } catch (err) {
      setProblem(signInProblem(err));
    } finally {
      setBusy(false);
      reread();
    }
  }

  const method = last ? methodOf(last.provider) : null;
  const how =
    method === "link" ? (
      <>
        <Icon name="mail" size={15} />
        Email link
      </>
    ) : method ? (
      <>
        <ProviderMark provider={method} size={15} />
        {PROVIDER_NAME[method]}
      </>
    ) : (
      (last?.provider ?? "Not recorded yet")
    );

  return (
    <div className="signin-grid">
      <section className="card" data-testid="signin-methods">
        <div className="card-h">
          <h2>How you sign in</h2>
        </div>
        {notice ? <LinkAlertView alert={notice} omniEmail={email} /> : null}
        <ul className="rows">
          <li>
            <div className="row method" data-method="link" data-testid="method-link">
              <span className="mtile mail">
                <Icon name="mail" size={17} />
              </span>
              <span className="row-main">
                <span className="row-t">
                  <b>Email link</b>
                  <Chip kind="ok">On</Chip>
                </span>
                <span className="row-m">Omni emails a link to {email}. Always on, so removing the others never locks you out.</span>
              </span>
            </div>
          </li>
          {PROVIDERS.map((provider) => {
            const P = PROVIDER_NAME[provider];
            const added = linked.find((l) => l.provider === provider);
            return (
              <li key={provider}>
                <div className="row method" data-method={provider} data-testid={`method-${provider}`}>
                  <span className="mtile">
                    <ProviderMark provider={provider} />
                  </span>
                  <span className="row-main">
                    <span className="row-t">
                      <b>{P}</b>
                      {added ? <Chip kind="ok">Added</Chip> : <Chip kind="quiet">Not added</Chip>}
                    </span>
                    {added ? (
                      <>
                        <span className="row-m">{added.email ?? email}</span>
                        {provider === "microsoft" ? <span className="row-m">Work or school account</span> : null}
                      </>
                    ) : (
                      <span className="row-m">
                        {provider === "microsoft" ? "Work or school accounts only. " : ""}Once it&rsquo;s added, Continue with {P} signs you in.
                      </span>
                    )}
                  </span>
                  <span className="row-end">
                    {added ? (
                      <button className="btn small" type="button" data-testid={`method-remove-${provider}`} disabled={busy} onClick={() => setSheet({ kind: "remove", provider })}>
                        <Icon name="trash-2" size={13} />
                        Remove
                      </button>
                    ) : (
                      <button className="btn small" type="button" data-testid={`method-add-${provider}`} disabled={busy} onClick={() => void add(provider)}>
                        <Icon name="plus" size={13} />
                        Add {P}
                      </button>
                    )}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="card-note">Google and Microsoft have to use {email}, the email your invite went to.</p>
      </section>
      <div>
        {twoStep ? (
          <section className="card" data-two-step="on" data-testid="two-step">
            <div className="card-h">
              <h2>Two-step sign-in</h2>
              <Chip kind="ok" icon="check">
                On
              </Chip>
            </div>
            <div className="two-step-on">
              <span className="mtile lock">
                <Icon name="smartphone" size={17} />
              </span>
              <span className="row-main">
                <b>Authenticator app</b>
                <span className="row-m">Set up {dayAt(twoStep.enrolledAt)}. Every sign-in asks for a code from it, whichever way you sign in.</span>
              </span>
            </div>
            <div className="btnrow" style={{ marginTop: 12 }}>
              <button className="btn small danger" type="button" data-testid="two-step-off" onClick={() => setSheet({ kind: "totp-off" })}>
                Turn off two-step
              </button>
            </div>
          </section>
        ) : (
          <section className="card" data-two-step="off" data-testid="two-step">
            <div className="card-h">
              <h2>Two-step sign-in</h2>
              <Chip kind="quiet">Off</Chip>
            </div>
            <p className="lede">Every sign-in also asks for a 6-digit code from an authenticator app on your phone, whichever way you sign in.</p>
            <div className="btnrow" style={{ marginTop: 12 }}>
              <button className="btn primary small" type="button" data-testid="two-step-setup" onClick={() => setSheet({ kind: "totp" })}>
                <Icon name="shield-check" size={14} />
                Set up two-step
              </button>
            </div>
          </section>
        )}
        <section className="card" data-last-sign-in="" data-testid="last-sign-in">
          <div className="card-h">
            <h2>Last sign-in</h2>
          </div>
          <dl className="facts one">
            <div>
              <dt>When</dt>
              <dd>{last ? cap(dayAt(last.at)) : "Not recorded yet"}</dd>
            </div>
            <div>
              <dt>How</dt>
              <dd>{how}</dd>
            </div>
            <div>
              <dt>Two-step</dt>
              <dd>{last?.secondFactor ? "Code entered" : twoStep ? "Set up since" : "Not set up"}</dd>
            </div>
          </dl>
        </section>
      </div>
      {sheet?.kind === "remove" ? (
        <Sheet
          title={`Remove ${PROVIDER_NAME[sheet.provider]}?`}
          onClose={() => setSheet(null)}
          foot={
            <>
              <button className="btn" type="button" onClick={() => setSheet(null)}>
                Keep it
              </button>
              <button
                className="btn stopfill"
                type="button"
                data-testid="remove-confirm"
                disabled={busy}
                onClick={() => void run(() => auth.unlink(sheet.provider), `${PROVIDER_NAME[sheet.provider]} removed. The email link still signs you in.`)}
              >
                Remove {PROVIDER_NAME[sheet.provider]}
              </button>
            </>
          }
        >
          <p>
            Continue with {PROVIDER_NAME[sheet.provider]} stops signing you in as {email}. The {PROVIDER_NAME[sheet.provider]} account itself doesn&rsquo;t change.
          </p>
          <p className="card-note">The email link still works, and you can add {PROVIDER_NAME[sheet.provider]} again any time.</p>
          {problem ? (
            <Callout kind="stop" icon="triangle-alert">
              {problem}
            </Callout>
          ) : null}
        </Sheet>
      ) : null}
      {sheet?.kind === "totp-off" ? (
        <Sheet
          title="Turn off two-step?"
          onClose={() => setSheet(null)}
          foot={
            <>
              <button className="btn" type="button" onClick={() => setSheet(null)}>
                Keep it on
              </button>
              <button className="btn stopfill" type="button" data-testid="two-step-off-confirm" disabled={busy} onClick={() => void run(() => auth.stopTwoStep(), "Two-step is off.")}>
                Turn off
              </button>
            </>
          }
        >
          <p>Sign-ins stop asking for a code from your authenticator app. You can set it up again any time, with a new code to scan.</p>
          {problem ? (
            <Callout kind="stop" icon="triangle-alert">
              {problem}
            </Callout>
          ) : null}
        </Sheet>
      ) : null}
      {sheet?.kind === "totp" ? (
        <TwoStepSheet
          onClose={() => setSheet(null)}
          onDone={() => {
            setSheet(null);
            reread();
            toast("Two-step is on. Every sign-in now asks for a code.");
          }}
        />
      ) : null}
    </div>
  );
}

/** Settings, Sign-in while acting as someone (mock M2 state 37): theirs to see; Stop brings back the admin's own. */
export function SignInHidden({ name }: { name: string }): React.ReactElement {
  const { stopActing } = useSession();
  const toast = useToast();
  const [stopping, setStopping] = useState(false);
  const first = firstName(name);
  function stop(): void {
    setStopping(true);
    stopActing("/settings/signin").then(
      (line) => {
        if (line) toast(line);
      },
      (err: unknown) => {
        setStopping(false);
        toast(`Omni did not stop acting: ${errorText(err)}`);
      },
    );
  }
  return (
    <section className="card" data-testid="signin-hidden">
      <div className="hidden-note">
        <span className="state-ic warn" aria-hidden="true">
          <Icon name="eye-off" size={20} />
        </span>
        <div>
          <h2>Sign-in is hidden while you act as {first}</h2>
          <p className="lede">
            How {first} signs in, two-step included, is {first}&rsquo;s to see and change. Stop acting as {first} to see your own.
          </p>
          <div className="btnrow" style={{ marginTop: 12 }}>
            <button className="btn primary small" type="button" data-testid="signin-hidden-stop" disabled={stopping} onClick={stop}>
              <Icon name="x" size={14} />
              Stop acting as {first}
            </button>
            <Link className="btn small" to="/settings">
              Back to Settings
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Settings while acting (mock M2 state 36, with Connect an AI kept as spec 3.9 has it): why Sign-in isn't among the tabs. */
export function ActingSignInNote({ name }: { name: string }): React.ReactElement {
  const first = firstName(name);
  return (
    <div className="callout warn titled" data-testid="acting-signin-note">
      <Icon name="eye-off" size={15} />
      <span>
        <b>Sign-in is hidden while you act as {first}</b>
        It&rsquo;s {first}&rsquo;s own, and Connect an AI or app is off while you act. Stop acting as {first} to see yours.
      </span>
    </div>
  );
}

/** Your accounts' Add Microsoft sign-in (mock M3 state 06): the popup, kept only for the person's own Omni email. */
export function AddMicrosoftSheet({ onClose, onAdded }: { onClose: () => void; onAdded: () => Promise<void> }): React.ReactElement {
  const { me, auth } = useSession();
  const [alert, setAlert] = useState<LinkAlert | null>(null);
  const [busy, setBusy] = useState(false);
  const email = me.user.email;
  async function add(): Promise<void> {
    setBusy(true);
    setAlert(null);
    const outcome = await addProvider(auth, "microsoft", email);
    if (outcome.kind === "added") await onAdded();
    else if (outcome.kind === "refused") setAlert(outcome.alert);
    setBusy(false);
  }
  return (
    <Sheet
      title="Add Microsoft sign-in"
      onClose={onClose}
      foot={
        <button className="btn" type="button" onClick={onClose}>
          Close
        </button>
      }
    >
      <p className="lede">
        Microsoft opens in a popup. Sign in with your work or school account, <b>{email}</b>. Personal Microsoft accounts can&rsquo;t be added.
      </p>
      {alert ? <LinkAlertView alert={alert} omniEmail={email} /> : null}
      <div className="signin-actions add-ms">
        <IdpButton provider="microsoft" label="Add Microsoft" testId="add-microsoft-go" disabled={busy} onClick={() => void add()} />
      </div>
    </Sheet>
  );
}
